import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0ActionItem, F0Button, F0Icon } from '@factorialco/f0-react'
import { CheckCircle, Files, Receipt, Warning } from '@factorialco/f0-react/icons/app'
import { DiscardDuplicateAlertDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { DuplicateExpenseAlert } from 'modules/expenses/hooks/useExpenseAlerts'
import { Expensable } from 'modules/expenses/types/Expensable'

import { DuplicateExpensesModal } from './_Alerts/_DuplicateExpensesCard/_Modal'

interface Flag {
  title: string
  description: string
}

interface PolicyFlagsProps {
  loading?: boolean
  flags?: Flag[]
  relevantPolicies?: string[]
  duplicateAlerts?: DuplicateExpenseAlert[]
  perDiemConflictAlerts?: DuplicateExpenseAlert[]
  expensable?: Expensable
  canOverrideApprovalFlow?: boolean
}

const PolicyFlags = ({
  loading = false,
  flags,
  relevantPolicies,
  duplicateAlerts = [],
  perDiemConflictAlerts = [],
  expensable,
  canOverrideApprovalFlow,
}: PolicyFlagsProps) => {
  const [selectedAlert, setSelectedAlert] = useState<DuplicateExpenseAlert | null>(null)
  const [selectedAlertType, setSelectedAlertType] = useState<
    'duplicate_alerts' | 'per_diem_conflict'
  >('duplicate_alerts')
  const [showComparisonModal, setShowComparisonModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)

  const {
    access: { locale },
    company: { id: companyId },
  } = useCurrentSession()

  const { onReject, loading: isRejecting } = useExpensableActions()

  const [discardDuplicateAlerts, { loading: isDiscarding }] = useMutation(
    DiscardDuplicateAlertDocument,
    {
      extractorFn: (data) => data.expensesMutations.bulkDiscardExpensesSpendingAlertSpendingAlert,
      update: (cache, { data }) => {
        const discardedAlerts =
          data?.expensesMutations.bulkDiscardExpensesSpendingAlertSpendingAlert.spendingAlerts || []
        discardedAlerts.forEach((alert) => {
          if (alert) {
            cache.evict({ id: cache.identify(alert) })
          }
        })
        cache.gc()
      },
    }
  )

  const closeModals = () => {
    setShowComparisonModal(false)
    setShowDiscardModal(false)
    setSelectedAlert(null)
  }

  const handleRejectExpense = async (expensableId: string, reason: string, _isTarget: boolean) => {
    if (!expensable) return
    const handled = await onReject([expensableId], reason, canOverrideApprovalFlow)
    if (handled.success) {
      setSelectedAlert(null)
    }
  }

  const removeAlerts = () => {
    if (selectedAlert) {
      discardDuplicateAlerts({
        variables: { alertIds: [selectedAlert.id], companyId },
      }).then((result) => {
        const handled = handleResult(result, () => {})
        if (handled.success) {
          closeModals()
          app.setFlash({
            type: 'correct',
            message:
              selectedAlertType === 'duplicate_alerts'
                ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_success')
                : i18n.t('expenses_v2.expense.per_diem_conflict_alerts.discard_success'),
            time: 2000,
          })
        }
      })
    }
  }

  if (loading) {
    return (
      <Box padding="s16" gap="s16" background="white">
        <F0ActionItem
          title={i18n.t('expenses_v2.expense.policy_flags.checking_policy')}
          status={'executing'}
        />
      </Box>
    )
  }

  if (!flags || !relevantPolicies) {
    return null
  }

  const allItems: Array<
    | { type: 'flag'; key: string; flag: Flag }
    | {
        type: 'compare'
        key: string
        alert: DuplicateExpenseAlert
        alertType: 'duplicate_alerts' | 'per_diem_conflict'
      }
  > = [
    ...flags.map((flag, index) => ({
      type: 'flag' as const,
      key: `flag-${index}`,
      flag,
    })),
    ...duplicateAlerts.map((alert) => ({
      type: 'compare' as const,
      key: `dup-${alert.id}`,
      alert,
      alertType: 'duplicate_alerts' as const,
    })),
    ...perDiemConflictAlerts.map((alert) => ({
      type: 'compare' as const,
      key: `pdc-${alert.id}`,
      alert,
      alertType: 'per_diem_conflict' as const,
    })),
  ]

  // In-policy and no other alerts
  if (allItems.length === 0) {
    return (
      <Box padding="s16" background="white">
        <Box
          padding="s20"
          gap="s8"
          style={{
            border: '1px solid #bbf7d0',
            backgroundColor: '#f0fdf4',
            borderRadius: '16px',
          }}
        >
          <Box flexDirection="row" alignItems="center" gap="s4">
            <Box style={{ color: '#16a34a' }}>
              <F0Icon icon={CheckCircle} size="md" />
            </Box>
            <Text.Normal weight="bold" color="grey1000">
              {i18n.t('expenses_v2.expense.policy_flags.meets_requirements')}
            </Text.Normal>
          </Box>
          <Text.Normal color="grey700">
            {i18n.t('expenses_v2.expense.policy_flags.expense_valid')}
          </Text.Normal>
        </Box>
      </Box>
    )
  }

  // Alerts found: outer orange box with white content box on top
  return (
    <Box padding="s16" background="white">
      {/* Outer orange box */}
      <Box
        style={{
          backgroundColor: '#fff1e0',
          borderRadius: '16px',
        }}
      >
        {/* Orange header */}
        <Box flexDirection="row" alignItems="center" gap="s8" paddingX="s16" paddingY="s12">
          <Box style={{ color: '#f97316' }}>
            <F0Icon icon={Warning} size="md" />
          </Box>
          <Text.Normal weight="medium" color="tangerine200">
            {i18n.t('expenses_v2.expense.policy_flags.alerts_summary')}
          </Text.Normal>
        </Box>

        {/* Inner white box with border */}
        <Box
          paddingX="s16"
          paddingY="s12"
          gap="s16"
          style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
          }}
        >
          {allItems.map((item) => {
            if (item.type === 'flag') {
              return (
                <Box key={item.key} gap="s4">
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <Box style={{ color: '#6B7280' }}>
                      <F0Icon icon={Receipt} size="md" />
                    </Box>
                    <Text.Normal weight="bold" color="grey1000">
                      {item.flag.title}
                    </Text.Normal>
                  </Box>
                  <Text.Normal color="grey700">{item.flag.description}</Text.Normal>
                </Box>
              )
            }

            const isDuplicate = item.alertType === 'duplicate_alerts'
            const title = isDuplicate
              ? i18n.t('expenses_v2.expense.duplicate_alerts.title')
              : i18n.t('expenses_v2.expense.per_diem_conflict_alerts.title')
            const description = isDuplicate
              ? i18n.t('expenses_v2.expense.duplicate_alerts.description', {
                  count: duplicateAlerts.length,
                })
              : i18n.t('expenses_v2.expense.per_diem_conflict_alerts.description', {
                  count: perDiemConflictAlerts.length,
                })

            return (
              <Box key={item.key} gap="s4">
                <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <Box style={{ color: '#6B7280' }}>
                      <F0Icon icon={Receipt} size="md" />
                    </Box>
                    <Text.Normal weight="bold" color="grey1000">
                      {title}
                    </Text.Normal>
                  </Box>
                  <F0Button
                    variant="outline"
                    size="md"
                    icon={Files}
                    label={i18n.t('expenses_v2.expense.policy_flags.compare')}
                    onClick={() => {
                      setSelectedAlert(item.alert)
                      setSelectedAlertType(item.alertType)
                      setShowComparisonModal(true)
                    }}
                  />
                </Box>
                <Text.Normal color="grey700">{description}</Text.Normal>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Comparison modal */}
      {selectedAlert && showComparisonModal && expensable && (
        <DuplicateExpensesModal
          locale={locale}
          alert={selectedAlert}
          expensable={expensable}
          type={selectedAlertType}
          isDiscarding={isDiscarding}
          isRejecting={isRejecting}
          onClose={closeModals}
          onRemoveAlert={() => setShowDiscardModal(true)}
          onRejectExpense={handleRejectExpense}
          canOverrideApprovalFlow={canOverrideApprovalFlow}
        />
      )}

      {/* Discard confirmation modal */}
      {showDiscardModal && (
        <Modal
          illustration="alert"
          style={{ position: 'relative', top: '200px' }}
          title={
            selectedAlertType === 'duplicate_alerts'
              ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.title')
              : i18n.t(
                  'expenses_v2.expense.per_diem_conflict_alerts.discard_confirmation.title_all'
                )
          }
          footer={
            <Box flexDirection="row" gap="s12">
              <F0Button variant="outline" label={i18n.t('common.cancel')} onClick={closeModals} />
              <F0Button
                label={i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.confirm')}
                onClick={removeAlerts}
                disabled={isDiscarding}
              />
            </Box>
          }
          onClose={closeModals}
        >
          <Text size="300" color="grey800">
            {selectedAlertType === 'duplicate_alerts'
              ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.description')
              : i18n.t(
                  'expenses_v2.expense.per_diem_conflict_alerts.discard_confirmation.description'
                )}
          </Text>
        </Modal>
      )}
    </Box>
  )
}

export default PolicyFlags
